import { Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type FinderApplicationPrivateModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/FinderApplicationPrivateModuleWithPermissionsSpecs';
// eslint-disable-next-line max-len
import { type FinderApplicationPublicModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/FinderApplicationPublicModuleWithPermissionsSpecs';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import FinderApplicationModuleActions from './FinderApplicationModuleActions';
import FinderApplicationModuleFooter from './FinderApplicationModuleFooter';
import FinderApplicationModuleInnerPage from './FinderApplicationModuleInnerPage';
import { HeaderTabs } from './type';

export type FinderApplicationModulePageProps = {
    module:
        | FinderApplicationPublicModuleWithPermissionsSpecsFragment
        | FinderApplicationPrivateModuleWithPermissionsSpecsFragment;
};

const FinderApplicationModulePage = ({ module }: FinderApplicationModulePageProps) => {
    const { t } = useTranslation(['finderApplicationModuleDetails']);
    const navigate = useNavigate();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: permissions.hasUpdatePermission,
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [module.id, permissions.hasUpdatePermission]
    );

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('finderApplicationModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('finderApplicationModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('finderApplicationModuleDetails:content.vehicleModule')}>
                {module.vehicleModule.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('finderApplicationModuleDetails:content.customerModule')}>
                {`${module.customerModule.displayName} - ${module.kycPresets
                    .map(kycPreset => kycPreset.displayName)
                    .join(', ')}`}
            </Descriptions.Item>
            {module.bankModule && (
                <Descriptions.Item label={t('finderApplicationModuleDetails:content.financingModule')}>
                    {module.bankModule.displayName}
                </Descriptions.Item>
            )}
            {module.bankModule ? (
                <Descriptions.Item label={t('finderApplicationModuleDetails:content.consentModule')}>
                    {module.agreementsModule.displayName}
                </Descriptions.Item>
            ) : null}
            {module.market ? (
                <Descriptions.Item label={t('finderApplicationModuleDetails:content.market')}>
                    {module.market}
                </Descriptions.Item>
            ) : null}

            {module.appointmentModule ? (
                <Descriptions.Item label={t('finderApplicationModuleDetails:content.appointment')}>
                    {module.appointmentModule.displayName}
                </Descriptions.Item>
            ) : null}
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <ConsolePageWithHeader
                content={content}
                extra={<FinderApplicationModuleActions module={module} permissions={permissions} />}
                footer={[<FinderApplicationModuleFooter activeTab={activeTab} />]}
                header={{
                    footer: (
                        <Tabs
                            activeKey={activeTab}
                            items={[
                                {
                                    label: t('finderApplicationModuleDetails:tabs.mainDetails'),
                                    key: HeaderTabs.MainDetails,
                                },
                                {
                                    label: t('finderApplicationModuleDetails:tabs.vehicles.title'),
                                    key: HeaderTabs.Vehicles,
                                },
                                {
                                    label: t('finderApplicationModuleDetails:tabs.financeProducts.title'),
                                    key: HeaderTabs.FinanceProducts,
                                },
                                {
                                    label: t('finderApplicationModuleDetails:tabs.email'),
                                    key: HeaderTabs.Email,
                                },
                            ]}
                            onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                        />
                    ),
                }}
                onBack={() => navigate('/admin/system/modules')}
                title={
                    screens.md
                        ? t('finderApplicationModuleDetails:title', { name: module.displayName })
                        : module.displayName
                }
            >
                <FinderApplicationModuleInnerPage activeTab={activeTab} module={module} />
            </ConsolePageWithHeader>
        </GenericStateOnPageContextProvider>
    );
};

export default FinderApplicationModulePage;
